import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Card, Tag, Button, Spin, Breadcrumb, Row, Col } from 'antd';
import { Helmet } from 'react-helmet-async';
import {
    ShoppingCartOutlined,
    CheckCircleOutlined,
    SafetyOutlined,
    HomeOutlined,
    WhatsAppOutlined
} from '@ant-design/icons';
import MainLayout from '../layouts/MainLayout';
import ProductCard from '../components/ProductCard';
import axios from 'axios';

const { TabPane } = Tabs;

const ProductDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<any>(null);
    const [similarProducts, setSimilarProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/products/${slug}`);
                setProduct(response.data.product);
                setSimilarProducts(response.data.similarProducts || []);
                // Sayfa yüklendiğinde en üste scroll et
                window.scrollTo(0, 0);
            } catch (error: any) {
                console.error('Ürün detayı yüklenemedi:', error);
                // 404 sayfasına yönlendir
                navigate('/lastik');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchProduct();
        }
    }, [slug, navigate]);

    if (loading) {
        return (
            <MainLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <Spin size="large" tip="Ürün detayları yükleniyor..." />
                </div>
            </MainLayout>
        );
    }

    if (!product) {
        return null;
    }

    const createSlug = (text: string) => {
        if (!text) return '';
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/ı/g, 'i')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/--+/g, '-');
    };

    const productUrl = `https://www.sakaryalastik.com/lastik/${createSlug(product.name)}-${product._id}`;

    // Ürün resmini al (downloaded_images varsa onu kullan, yoksa image field'ını kullan)
    const getProductImage = () => {
        if (product.downloaded_images && product.downloaded_images.length > 1) {
            // İlk resim logo, ikinci resim ürün
            return `/uploads/${product.downloaded_images[1].replace(/\.(png|jpg|jpeg|gif)$/i, '.webp')}`;
        } else if (product.downloaded_images && product.downloaded_images.length > 0) {
            return `/uploads/${product.downloaded_images[0].replace(/\.(png|jpg|jpeg|gif)$/i, '.webp')}`;
        } else if (product.image) {
            return product.image;
        }
        return '/images/logo.webp';
    };

    // Brand logosu al
    const getBrandLogo = () => {
        if (product.downloaded_images && product.downloaded_images.length > 0) {
            return `/uploads/${product.downloaded_images[0].replace(/\.(png|jpg|jpeg|gif)$/i, '.webp')}`;
        }
        return null;
    };

    const imageUrl = getProductImage();
    const brandLogo = getBrandLogo();

    return (
        <MainLayout>
            <Helmet>
                <title>{product.name} | Sakarya Lastik - Fiyat ve Detaylar</title>
                <meta name="description" content={`${product.brand_name} ${product.tire_size} lastik fiyatı ve özellikleri. ${product.properties?.season || ''} lastiği. Online sipariş ve hızlı teslimat.`} />
                <meta name="keywords" content={`${product.brand_name}, ${product.tire_size}, lastik fiyatı, ${product.properties?.season} lastiği, lastik satış`} />

                {/* Open Graph */}
                <meta property="og:title" content={`${product.name} - Sakarya Lastik`} />
                <meta property="og:description" content={`${product.brand_name} ${product.tire_size} lastik fiyatı: ${product.price}₺`} />
                <meta property="og:image" content={imageUrl} />
                <meta property="og:url" content={productUrl} />
                <meta property="og:type" content="product" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={product.name} />
                <meta name="twitter:description" content={`${product.brand_name} ${product.tire_size} - ${product.price}₺`} />
                <meta name="twitter:image" content={imageUrl} />

                {/* Canonical */}
                <link rel="canonical" href={productUrl} />
            </Helmet>

            {/* Product Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": product.name,
                    "image": imageUrl,
                    "description": `${product.brand_name} ${product.tire_size} lastik`,
                    "brand": {
                        "@type": "Brand",
                        "name": product.brand_name
                    },
                    "offers": {
                        "@type": "Offer",
                        "url": productUrl,
                        "priceCurrency": "TRY",
                        "price": product.price,
                        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        "itemCondition": "https://schema.org/NewCondition",
                        "availability": "https://schema.org/InStock",
                        "seller": {
                            "@type": "Organization",
                            "name": "Sakarya Lastik"
                        }
                    }
                })}
            </script>

            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <Breadcrumb className="mb-6">
                    <Breadcrumb.Item href="/">
                        <HomeOutlined /> Ana Sayfa
                    </Breadcrumb.Item>
                    <Breadcrumb.Item href="/lastik">
                        Lastikler
                    </Breadcrumb.Item>
                    {product.category && (
                        <Breadcrumb.Item href={`/lastik?category=${encodeURIComponent(
                            typeof product.category === 'object' ? product.category.name : product.category
                        )}`}>
                            {typeof product.category === 'object' ? product.category.name : product.category}
                        </Breadcrumb.Item>
                    )}
                    <Breadcrumb.Item href={`/lastik?brand=${encodeURIComponent(product.brand_name)}`}>
                        {product.brand_name}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{product.tire_size}</Breadcrumb.Item>
                </Breadcrumb>

                <Row gutter={[32, 32]}>
                    {/* Sol: Ürün Görseli */}
                    <Col xs={24} lg={10}>
                        <Card className="shadow-lg overflow-hidden">
                            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8">
                                {/* Brand Logo Overlay */}
                                {brandLogo && (
                                    <div className="absolute top-4 left-4 bg-white rounded-lg shadow-md px-3 py-2 z-10">
                                        <img
                                            src={brandLogo}
                                            alt={`${product.brand_name} Logo`}
                                            className="h-8 w-auto object-contain"
                                        />
                                    </div>
                                )}

                                {/* Stok Durumu Badge */}
                                {product.stock_amount !== undefined && (
                                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold shadow-md z-10 ${product.stock_amount > 0
                                        ? 'bg-green-500 text-white'
                                        : 'bg-red-500 text-white'
                                        }`}>
                                        Stok Sorunuz
                                    </div>
                                )}

                                {/* Ürün Resmi */}
                                <div className="flex items-center justify-center">
                                    <img
                                        src={imageUrl}
                                        alt={product.name}
                                        className="max-w-full h-auto max-h-96 object-contain drop-shadow-2xl"
                                        onError={(e: any) => {
                                            e.target.src = '/images/logo.webp';
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Hızlı Bilgiler - Resmin Altında */}
                            <div className="grid grid-cols-3 gap-2 p-4 bg-gray-50 border-t">
                                <div className="text-center p-2 bg-white rounded-lg">
                                    <div className="text-xs text-gray-500">Üretim</div>
                                    <div className="font-bold text-gray-800">{product.production_year || 'N/A'}</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded-lg">
                                    <div className="text-xs text-gray-500">Mevsim</div>
                                    <div className="font-bold text-gray-800">{product.properties?.season || 'N/A'}</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded-lg">
                                    <div className="text-xs text-gray-500">Tip</div>
                                    <div className="font-bold text-gray-800 text-xs">{product.tire_type || 'N/A'}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    {/* Sağ: Ürün Bilgileri */}
                    <Col xs={24} lg={14}>
                        <Card className="shadow-lg">
                            {/* Başlık ve Yıldızlar */}
                            <div className="mb-4">
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                                    {product.name}
                                </h1>
                                <div className="flex items-center gap-2">
                                    <span className="text-yellow-400">★★★★★</span>
                                    <span className="text-sm text-gray-500">(Müşteri Değerlendirmeleri)</span>
                                </div>
                            </div>

                            {/* Marka ve Kategori Tags */}
                            <div className="flex flex-wrap gap-2 mb-6">
                                <Tag
                                    color="blue"
                                    className="cursor-pointer text-sm py-1 px-3"
                                    onClick={() => navigate(`/lastik?brand=${encodeURIComponent(product.brand_name)}`)}
                                >
                                    🏷️ {product.brand_name}
                                </Tag>
                                {product.category && (
                                    <Tag
                                        color="green"
                                        className="cursor-pointer text-sm py-1 px-3"
                                        onClick={() => navigate(`/lastik?category=${encodeURIComponent(
                                            typeof product.category === 'object' ? product.category.name : product.category
                                        )}`)}
                                    >
                                        📂 {typeof product.category === 'object' ? product.category.name : product.category}
                                    </Tag>
                                )}
                                {product.properties?.season && (
                                    <Tag color="orange" className="text-sm py-1 px-3">
                                        {product.properties.season === 'Kış' ? '❄️' :
                                            product.properties.season === 'Yaz' ? '☀️' : '🌤️'} {product.properties.season}
                                    </Tag>
                                )}
                                {product.tire_type && (
                                    <Tag color="purple" className="text-sm py-1 px-3">
                                        🚗 {product.tire_type}
                                    </Tag>
                                )}
                            </div>

                            {/* Lastik Ebatı - Büyük ve Belirgin */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border-l-4 border-blue-500">
                                <div className="text-sm text-gray-600 mb-1">Lastik Ebatı</div>
                                <div className="text-3xl font-bold text-gray-800 font-mono">
                                    {product.tire_size}
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                    {product.properties?.width}/{product.properties?.aspect_ratio} R{product.properties?.rim_diameter}
                                    {product.properties?.load_index && ` • ${product.properties.load_index}`}
                                    {product.properties?.speed_index && ` • ${product.properties.speed_index}`}
                                </div>
                            </div>

                            {/* Fiyat */}
                            <div className="bg-gradient-to-r from-orange-50 via-orange-100 to-red-50 rounded-xl p-6 mb-6 border-2 border-orange-200">
                                <div className="text-sm text-gray-600 mb-2">Fiyat</div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-5xl font-bold text-orange-600">
                                        ₺{product.price.toLocaleString('tr-TR')}
                                    </span>
                                    <span className="text-gray-600 text-lg">/adet</span>
                                    <span className="text-gray-400 text-2xl mx-2">|</span>
                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-xs">Taksitli</span>
                                        <span className="text-xl font-semibold text-gray-700">
                                            ₺{Math.round(product.price * 1.1).toLocaleString('tr-TR')}
                                        </span>
                                    </div>
                                </div>

                                {product.original_price && product.original_price > product.price && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <Tag color="red" className="text-base px-3 py-1">
                                            🔥 %{Math.round(((product.original_price - product.price) / product.original_price) * 100)} İNDİRİM
                                        </Tag>
                                        <span className="text-green-600 font-semibold">
                                            ₺{(product.original_price - product.price).toLocaleString('tr-TR')} Tasarruf!
                                        </span>
                                    </div>
                                )}
                                <div className="mt-3 text-sm text-gray-600">
                                    💳 Taksit imkanı için iletişime geçin
                                </div>
                            </div>

                            {/* Hızlı Özellikler Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-6">
                                {product.properties?.fuel_efficiency && (
                                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                                        <div className="text-xs text-green-700 mb-1">⛽ Yakıt Verimliliği</div>
                                        <div className="text-lg font-bold text-green-800">{product.properties.fuel_efficiency}</div>
                                    </div>
                                )}
                                {product.properties?.wet_grip && (
                                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                        <div className="text-xs text-blue-700 mb-1">💧 Islak Zemin Tutuşu</div>
                                        <div className="text-lg font-bold text-blue-800">{product.properties.wet_grip}</div>
                                    </div>
                                )}
                                {product.properties?.noise_decibel && (
                                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                                        <div className="text-xs text-purple-700 mb-1">🔊 Gürültü Seviyesi</div>
                                        <div className="text-lg font-bold text-purple-800">{product.properties.noise_decibel} dB</div>
                                    </div>
                                )}
                                <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                                    <div className="text-xs text-orange-700 mb-1">📦 Teslimat</div>
                                    <div className="text-lg font-bold text-orange-800">1-7 Gün</div>
                                </div>
                            </div>

                            {/* CTA Butonlar */}
                            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ShoppingCartOutlined />}
                                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-none h-16 text-xl font-bold shadow-lg"
                                    href="tel:+905441205454"
                                >
                                    📞 Hemen Sipariş Ver
                                </Button>
                                <Button
                                    size="large"
                                    icon={<WhatsAppOutlined />}
                                    className="flex-1 bg-green-500 hover:bg-green-600 text-white border-none h-16 text-lg font-semibold shadow-lg"
                                    href="https://wa.me/905441205454"
                                    target="_blank"
                                >
                                    WhatsApp
                                </Button>
                            </div>

                            {/* Güven Rozetleri - Daha Büyük ve Belirgin */}
                            <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-gray-200">
                                <div className="text-center">
                                    <div className="bg-white rounded-full w-16 h-16 mx-auto flex items-center justify-center mb-2 shadow-md">
                                        <CheckCircleOutlined className="text-4xl text-green-500" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800">Orijinal Ürün</p>
                                    <p className="text-xs text-gray-500">%100 Garantili</p>
                                </div>
                                <div className="text-center">
                                    <div className="bg-white rounded-full w-16 h-16 mx-auto flex items-center justify-center mb-2 shadow-md">
                                        <SafetyOutlined className="text-4xl text-blue-500" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800">Garanti</p>
                                    <p className="text-xs text-gray-500">Üretici Garantili</p>
                                </div>
                                <div className="text-center">
                                    <div className="bg-white rounded-full w-16 h-16 mx-auto flex items-center justify-center mb-2 shadow-md">
                                        <ShoppingCartOutlined className="text-4xl text-orange-500" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800">Hızlı Teslimat</p>
                                    <p className="text-xs text-gray-500">Ücretsiz Kargo</p>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Detaylı Bilgiler - Tablar */}
                <div className="mt-8">
                    <Card className="shadow-lg">
                        <Tabs defaultActiveKey="specs" size="large">
                            <TabPane tab="Teknik Özellikler" key="specs">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div className="flex justify-between border-b pb-2">
                                            <span className="font-semibold text-gray-700">Lastik Ebatı:</span>
                                            <span className="text-gray-900">{product.tire_size}</span>
                                        </div>
                                        <div className="flex justify-between border-b pb-2">
                                            <span className="font-semibold text-gray-700">Marka:</span>
                                            <span className="text-gray-900">{product.brand_name}</span>
                                        </div>
                                        {product.properties?.season && (
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="font-semibold text-gray-700">Mevsim:</span>
                                                <span className="text-gray-900">{product.properties.season}</span>
                                            </div>
                                        )}
                                        {product.production_year && (
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="font-semibold text-gray-700">Üretim Yılı:</span>
                                                <span className="text-gray-900">{product.production_year}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-4">
                                        {product.properties?.load_index && (
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="font-semibold text-gray-700">Yük İndeksi:</span>
                                                <span className="text-gray-900">{product.properties.load_index}</span>
                                            </div>
                                        )}
                                        {product.properties?.speed_index && (
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="font-semibold text-gray-700">Hız İndeksi:</span>
                                                <span className="text-gray-900">{product.properties.speed_index}</span>
                                            </div>
                                        )}
                                        {product.tire_type && (
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="font-semibold text-gray-700">Lastik Tipi:</span>
                                                <span className="text-gray-900">{product.tire_type}</span>
                                            </div>
                                        )}
                                        {product.original_name && (
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="font-semibold text-gray-700">Model Adı:</span>
                                                <span className="text-gray-900">{product.original_name}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </TabPane>

                            <TabPane tab={`${product.brand_name} Hakkında`} key="brand">
                                <div className="prose max-w-none">
                                    <h3 className="text-2xl font-bold mb-4">{product.brand_name} Lastik Özellikleri</h3>
                                    <p className="text-gray-700 leading-relaxed">
                                        {product.brand_name} markası, dünya çapında tanınan ve güvenilen bir lastik üreticisidir.
                                        Yüksek kalite standartları ve ileri teknoloji kullanımıyla üretilen lastikler,
                                        güvenli sürüş deneyimi sunar.
                                    </p>
                                    <h4 className="text-xl font-semibold mt-6 mb-3">Neden {product.brand_name}?</h4>
                                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                        <li>Üstün kalite ve dayanıklılık</li>
                                        <li>Gelişmiş teknoloji ve Ar-Ge çalışmaları</li>
                                        <li>Geniş ürün yelpazesi</li>
                                        <li>Dünya çapında garanti ve destek</li>
                                        <li>Çevre dostu üretim süreçleri</li>
                                    </ul>
                                </div>
                            </TabPane>

                            <TabPane tab="Teslimat & Montaj" key="delivery">
                                <div className="prose max-w-none">
                                    <h3 className="text-2xl font-bold mb-4">Teslimat ve Montaj Hizmetleri</h3>

                                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                                        <p className="font-semibold text-blue-900">✓ Ücretsiz Kargo</p>
                                        <p className="text-blue-800 text-sm">Sakarya ve çevre illerde ücretsiz teslimat</p>
                                    </div>

                                    <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                                        <p className="font-semibold text-green-900">✓ Profesyonel Montaj</p>
                                        <p className="text-green-800 text-sm">Uzman ekibimiz tarafından montaj hizmeti</p>
                                    </div>

                                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                                        <p className="font-semibold text-orange-900">✓ Hızlı Teslimat</p>
                                        <p className="text-orange-800 text-sm">1-3 iş günü içinde teslimat</p>
                                    </div>

                                    <h4 className="text-xl font-semibold mt-6 mb-3">Sipariş Süreci</h4>
                                    <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                                        <li>Telefon veya WhatsApp ile sipariş verin</li>
                                        <li>Ödeme bilgilerinizi paylaşın</li>
                                        <li>Teslimat tarihini belirleyin</li>
                                        <li>Lastikleriniz adresinize ulaştırılsın</li>
                                        <li>Profesyonel montaj hizmeti alın</li>
                                    </ol>
                                </div>
                            </TabPane>
                        </Tabs>
                    </Card>
                </div>

                {/* Benzer Ürünler */}
                {similarProducts.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold mb-6">
                            {product.brand_name} Markasından Benzer Ürünler
                        </h2>
                        <div className="flex flex-wrap gap-4 justify-start">
                            {similarProducts.map((item: any) => (
                                <ProductCard key={item._id} product={item} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default ProductDetail;
